import { Form, Modal } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useState } from "react";

import type { SiteEntity } from "src/app/features/Site/types/SiteEntity";
import { useUpdateTableDataMutation } from "../../../../../components";
import { ActionBar } from "../../../../../components/ActionBar/ActionBar";
import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { RenderImportResult } from "../../../../../components/FeatureButtons/csvModalButtonUtils";
import type { ButtonProps, ImportResult } from "../../../../../components/FeatureButtons/types";
import { Checkbox } from "../../../../../components/Form/formComponents";
import { ProgressBar } from "../../../../../components/ProgressBar/ProgressBar";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const DealerChangeButton: FC<ButtonProps<SiteEntity>> = ({ item, items }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const isDisabled = items?.length !== 1;

    const [form] = Form.useForm();

    const currentDataGroup = getAssignedDataGroup();
    const dealerChangeUrl = `${ResourceName.DATA_GROUPS}/${currentDataGroup.code}/${ResourceName.SITES}/${item?.id}/${FORM_ACTION.DEALER_CHANGE}`;

    const [dealerChangeRequest] = useUpdateTableDataMutation();

    const showModal = useCallback(() => setIsModalVisible(true), []);
    const onCancel = useCallback(() => setIsModalVisible(false), []);

    const onOk = useCallback(async () => {
        setUploading(true);

        try {
            const values = await form.validateFields();

            const body = {
                copyTerminals: values.copyTerminals,
                copyContracts: values.destinationDataGroupId,
            };

            dealerChangeRequest({
                url: dealerChangeUrl,
                method: "POST",
                body,
            })
                .unwrap()
                // @ts-ignore
                .then((result) => setImportResult(result))
                .catch((error) => {
                    setImportResult({
                        importErrors: [error?.data?.message || "dealerChange.modal.importError"],
                    });
                })
                .finally(() => setUploading(false));
        } catch (error) {
            setImportResult({
                importErrors: [typeof error === "string" ? error : "dealerChange.modal.importError"],
            });
            setUploading(false);
        }
    }, [form, dealerChangeRequest, dealerChangeUrl]);

    return (
        <>
            <SecondaryButton onClick={showModal} disabled={isDisabled}>
                {i18n.t("button.dealerChange")}
            </SecondaryButton>

            <Modal
                open={isModalVisible}
                title={i18n.t("dealerChange.modal.title")}
                onOk={onOk}
                onCancel={onCancel}
                footer={[
                    <SecondaryButton key="close" onClick={onCancel}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton key="import" onClick={onOk} disabled={uploading}>
                        {i18n.t("button.save")}
                    </PrimaryButton>,
                ]}
            >
                <Form form={form}>
                    <ActionBar className="column">
                        <Form.Item name="copyTerminals" valuePropName="checked">
                            <Checkbox label="dealerChange.copyTerminals" readOnly={false} defaultChecked={false} name="copyTerminals" />
                        </Form.Item>
                        <Form.Item name="copyContracts" valuePropName="checked">
                            <Checkbox label="dealerChange.copyContracts" readOnly={false} defaultChecked={false} name="copyContracts" />
                        </Form.Item>
                        {uploading && <ProgressBar />}
                        {importResult && (
                            <RenderImportResult
                                importResult={importResult}
                                showAllErrors={showAllErrors}
                                setShowAllErrors={setShowAllErrors}
                                successString="dealerChange.successfulOperation"
                                errorString="dealerChange.failedOperation"
                            />
                        )}
                    </ActionBar>
                </Form>
            </Modal>
        </>
    );
};
